/**
 * Layout System
 * 
 * Centralized layout registry for content display.
 * Similar to reference Vue implementation in app/src/layouts/
 * 
 * Layouts can be shared across modules:
 * - Content module (default: tabular)
 * - Users module (default: cards)
 * - Files module (default: cards)
 * - Activity module (default: tabular)
 */

import { tabularLayout } from './tabular';
import { cardsLayout } from './cards';
import type {
    LayoutId,
    LayoutConfig,
    LayoutRegistry,
    LayoutProps,
    BaseLayoutOptions,
    TabularLayoutOptions,
    CardsLayoutOptions,
    LayoutQuery,
    LayoutItem,
    LayoutSelection,
    LayoutOptionsProps,
    LayoutActionsProps,
} from './types';

// Layout registry - all available layouts
export const layouts: LayoutRegistry = {
    tabular: tabularLayout,
    cards: cardsLayout,
    // Future layouts can be added here:
    // calendar: calendarLayout,
    // kanban: kanbanLayout,
    // map: mapLayout,
} as LayoutRegistry;

// Get a specific layout configuration
export function getLayout(id: LayoutId): LayoutConfig | undefined {
    return layouts[id];
}

// Get all available layouts
export function getAvailableLayouts(): LayoutConfig[] {
    return Object.values(layouts);
}

// Get default layout for a module
export function getDefaultLayout(module: 'content' | 'users' | 'files' | 'activity'): LayoutId {
    switch (module) {
        case 'users':
        case 'files':
            return 'cards';
        case 'content':
        case 'activity':
        default:
            return 'tabular';
    }
}

// Re-export types
export type {
    LayoutId,
    LayoutConfig,
    LayoutRegistry,
    LayoutProps,
    BaseLayoutOptions,
    TabularLayoutOptions,
    CardsLayoutOptions,
    LayoutQuery,
    LayoutItem,
    LayoutSelection,
    LayoutOptionsProps,
    LayoutActionsProps,
};

// Re-export layout components
export { TabularLayout, TabularOptions } from './tabular';
export { CardsLayout, CardsOptions } from './cards';
export { LayoutWrapper, LayoutOptionsWrapper } from './LayoutWrapper';

// Re-export utilities
export { formatCellValue, renderTemplate, getValue, getDisplayValue } from './utils';
